<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import logoUrl from '@/assets/login-logo.png';
import AuthLayout from "@/layout/auth/AuthLayout.vue";
import { Head, Link, useForm } from '@inertiajs/vue3';
import GlobalLoadingOverlay from '@/components/GlobalLoadingOverlay.vue';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const { showPageLoading, hidePageLoading } = useEnhancedLoading();

const submit = () => {
    console.log('Login submit called');

    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('client.login'), {
        onStart: () => {
            console.log('onStart: Showing page loading');
            showPageLoading($t('Signing in...'));
        },
        onFinish: () => {
            console.log('onFinish: Hiding page loading');
            form.reset('password');
            hidePageLoading();
        },
        onError: () => {
            console.log('onError: Hiding page loading');
            hidePageLoading();
        }
    });
};

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const supportMenu = ref();
const supportItems = computed(() => ([
    {
        label: $t('Whatsapp'),
        icon: 'pi pi-whatsapp',
        url: 'https://wa.me/5547989104747?text=Ol%C3%A1%21+Preciso+de+ajuda+para+logar+no+sistema',
        target: '_blank'
    },
    {
        label: $t('Telegram'),
        icon: 'pi pi-telegram',
        url: 'https://t.me/maxpiress',
        target: '_blank'
    },
    {
        label: $t('Skype'),
        icon: 'pi pi-skype',
        url: 'https://msng.link/o/?maxpires=sk',
        target: '_blank'
    }
]));

defineOptions({
    layout: AuthLayout,
});

</script>

<template>
    <GlobalLoadingOverlay />
    <Head :title="$t('Login')" />

    <div class="border mx-2 sm:mx-auto px-5 sm:px-20 pb-16 rounded-2xl">
        <h1 class="text-4xl sm:text-5xl font-medium text-center mx-auto leading-[125%] my-12 sm:min-w-96">
            <img :src="logoUrl" class="h-16 w-auto mx-auto" alt="Logo"/>
        </h1>

        <form @submit.prevent="submit" class="max-w-[35rem] mx-auto">
            <div v-if="status" class="font-medium text-sm text-green-600 mb-4">
                {{ status }}
            </div>

            <div class="mb-6 flex flex-col gap-2">
                <FloatLabel variant="on">
                    <InputText
                        v-model="form.email"
                        type="email"
                        class="w-full"
                        required
                    />
                    <label for="locale" class="text-sm text-surface-500">{{ $t('Email') }} <span class="required-color">*</span></label>
                </FloatLabel>

                </div>

            <div class="flex flex-col gap-2">
                <FloatLabel variant="on">
                    <Password
                        v-model="form.password"
                        :feedback="false"
                        toggleMask
                        class="w-full"
                        :pt="{
                        pcInputText: {
                            root: {
                                class: '!w-full'
                            }
                        }
                    }"
                        required
                    />
                    <label for="locale" class="text-sm text-surface-500">{{ $t('Password') }} <span class="required-color">*</span></label>
                </FloatLabel>
                <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
            </div>

            <div class="space-y-8 mt-8 text-sm">
                <div class="flex items-center justify-between">
                    <Link
                        v-if="canResetPassword"
                        :href="route('client.password.request')"
                        class="text-primary-active hover:opacity-75 transition-all"
                    >
                        {{ $t('Forgot your password?') }}
                    </Link>
                </div>

                <div v-if="form.errors.email" class="text-red-500 text-sm text-center">
                    {{ form.errors.email }}
                </div>

                <Button
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    type="submit"
                    class="landing-button-primary w-full"
                >
                    {{ $t('Login') }}
                </Button>

                <div class="text-surface-500 text-center leading-6">
                    {{ $t('Not registered?') }}
                    <Link
                        :href="route('client.register')"
                        class="text-primary-active font-medium hover:opacity-75 transition-all"
                    >
                        {{ $t('Register') }}
                    </Link>
                </div>
            </div>
        </form>

        <!-- Contact Support Section -->
        <div class="mt-6 max-w-[35rem] mx-auto">
            <div class="relative">
                <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-300" />
                </div>
                <div class="relative flex justify-center text-sm">
                    <span class="bg-white px-2 text-gray-500">{{ $t('Need some help?') }}</span>
                </div>
            </div>

            <div class="mt-6">
                <div class="relative">
                    <button type="button"
                            class="inline-flex w-full justify-center items-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-500 shadow-sm hover:bg-gray-50"
                            @click="(e) => supportMenu && supportMenu.toggle(e)">
                        <span>{{ $t('Our team is here') }}</span>
                        <i class="pi pi-chevron-down ml-3"></i>
                    </button>
                    <Menu ref="supportMenu" :model="supportItems" :popup="true" class="w-48" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.p-error {
    @apply text-red-500 text-xs mt-1;
}
.required-color {
    color: red;
}
</style>
